import { useRef, useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { anyApi } from "convex/server";
import { useProperty } from "../lib/property-context";
import { Field, inputCls } from "./form-controls";

type Status = "pending" | "approved" | "denied";

interface ReceiptUrl {
  storageId: string;
  url: string | null;
}

interface ExpenseRequest {
  _id: string;
  amountCents: number;
  description: string;
  status: Status;
  createdAt: number;
  decidedAt: number | null;
  decisionNotes: string | null;
  receiptUrls: ReceiptUrl[];
  tenant: {
    _id: string;
    firstName: string | null;
    lastName: string | null;
    email: string;
  } | null;
  unit: { _id: string; label: string } | null;
}

// Dual-purpose section. Landlord sees the full list with decide
// actions; tenant sees their own list + a submit form. Both share
// the row component for layout consistency.
export function ExpenseRequestsSection({
  role,
}: {
  role: "landlord" | "tenant" | null | undefined;
}) {
  const property = useProperty();
  const isLandlord = role === "landlord";
  const isTenant = role === "tenant";

  const landlordList = useQuery(
    anyApi.expenseRequests.listForProperty,
    property && isLandlord ? { propertySlug: property.slug } : "skip",
  ) as ExpenseRequest[] | undefined;

  const tenantList = useQuery(
    anyApi.expenseRequests.listMine,
    property && isTenant ? { propertySlug: property.slug } : "skip",
  ) as ExpenseRequest[] | undefined;

  if (!property || (!isLandlord && !isTenant)) return null;

  const list = isLandlord ? landlordList : tenantList;
  const pendingCount = (list ?? []).filter((r) => r.status === "pending").length;

  return (
    <section className="rounded-xl border border-slate-200 bg-white">
      <header className="px-6 pt-5 pb-4 border-b border-slate-100 flex items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-slate-900">
            {isLandlord ? "Expense requests" : "Expense reimbursements"}
          </h2>
          <p className="mt-0.5 text-sm text-slate-500">
            {isLandlord
              ? "Tenant-paid expenses awaiting review."
              : "Submit out-of-pocket costs for reimbursement or rent credit."}
          </p>
        </div>
        {isLandlord && pendingCount > 0 && (
          <span className="inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-700 ring-1 ring-inset ring-amber-200">
            {pendingCount} pending
          </span>
        )}
      </header>
      <div className="px-6 py-4 space-y-4">
        {isTenant && property && <SubmitForm propertySlug={property.slug} />}
        {list === undefined ? (
          <p className="text-sm text-slate-500">Loading…</p>
        ) : list.length === 0 ? (
          <p className="text-sm text-slate-500">
            {isLandlord
              ? "No expense requests yet."
              : "You haven't submitted any expense requests."}
          </p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {list.map((r) => (
              <RequestRow
                key={r._id}
                request={r}
                role={isLandlord ? "landlord" : "tenant"}
              />
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

function SubmitForm({ propertySlug }: { propertySlug: string }) {
  const submit = useMutation(anyApi.expenseRequests.submit);
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setAmount("");
    setDescription("");
    setError(null);
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const dollars = Number(amount);
      if (!Number.isFinite(dollars) || dollars <= 0) {
        throw new Error("amount must be a positive number");
      }
      await submit({
        propertySlug,
        amountCents: Math.round(dollars * 100),
        description,
      });
      reset();
      setOpen(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="rounded-md border border-dashed border-slate-300 bg-white px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50 transition-colors"
      >
        + Submit an expense
      </button>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      className="rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-3"
    >
      <p className="text-xs font-medium uppercase tracking-[0.18em] text-slate-500">
        New expense
      </p>
      <Field label="Amount (USD)" required>
        <input
          className={inputCls}
          inputMode="decimal"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder="e.g., 42.50"
          autoFocus
        />
      </Field>
      <Field label="Description" required>
        <textarea
          className={`${inputCls} min-h-[80px]`}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="What was this for? (Add receipt photos after submitting.)"
        />
      </Field>
      {error && <p className="text-xs text-red-600">{error}</p>}
      <div className="flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={() => {
            setOpen(false);
            reset();
          }}
          disabled={busy}
          className="text-xs font-medium text-slate-600 hover:text-slate-900 px-3 py-1.5 rounded hover:bg-slate-100 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={busy}
          className="rounded-md bg-slate-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-800 transition-colors disabled:opacity-50"
        >
          {busy ? "Submitting…" : "Submit"}
        </button>
      </div>
    </form>
  );
}

function RequestRow({
  request,
  role,
}: {
  request: ExpenseRequest;
  role: "landlord" | "tenant";
}) {
  const decide = useMutation(anyApi.expenseRequests.decide);
  const cancel = useMutation(anyApi.expenseRequests.cancel);
  const generateReceiptUrl = useMutation(
    anyApi.expenseRequests.generateReceiptUploadUrl,
  );
  const attachReceipt = useMutation(anyApi.expenseRequests.attachReceipt);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [decisionNotesDraft, setDecisionNotesDraft] = useState("");
  const [decisionPick, setDecisionPick] = useState<
    null | "approved" | "denied"
  >(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const tenantName = (() => {
    if (!request.tenant) return "Unknown tenant";
    if (request.tenant.firstName) {
      return request.tenant.lastName
        ? `${request.tenant.firstName} ${request.tenant.lastName}`
        : request.tenant.firstName;
    }
    return request.tenant.email;
  })();

  const handleDecide = async (decision: "approved" | "denied") => {
    setBusy(true);
    setError(null);
    try {
      await decide({
        requestId: request._id,
        decision,
        notes: decisionNotesDraft || undefined,
      });
      setDecisionPick(null);
      setDecisionNotesDraft("");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const handleCancel = async () => {
    if (!confirm("Cancel this request?")) return;
    setBusy(true);
    setError(null);
    try {
      await cancel({ requestId: request._id });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const handleAttachReceipt = async (file: File | null) => {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      const uploadUrl = (await generateReceiptUrl({
        requestId: request._id,
      })) as string;
      const res = await fetch(uploadUrl, {
        method: "POST",
        headers: { "Content-Type": file.type || "application/octet-stream" },
        body: file,
      });
      if (!res.ok) throw new Error(`upload failed: ${res.status}`);
      const { storageId } = (await res.json()) as { storageId: string };
      await attachReceipt({ requestId: request._id, storageId });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  return (
    <li className="py-3 first:pt-0">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-semibold text-slate-900">
              ${(request.amountCents / 100).toFixed(2)}
            </p>
            <StatusBadge status={request.status} />
            {role === "landlord" && (
              <span className="text-xs text-slate-500">
                · {tenantName}
                {request.unit && ` · ${request.unit.label}`}
              </span>
            )}
          </div>
          <p className="mt-1 text-sm text-slate-700 whitespace-pre-wrap">
            {request.description}
          </p>
          <p className="mt-1 text-xs text-slate-500">
            Submitted {formatTimestamp(request.createdAt)}
            {request.decidedAt && (
              <>
                {" · "}
                {request.status === "approved" ? "Approved" : "Denied"}{" "}
                {formatTimestamp(request.decidedAt)}
              </>
            )}
          </p>
          {request.decisionNotes && (
            <p className="mt-1 text-xs text-slate-600 italic">
              "{request.decisionNotes}"
            </p>
          )}
          {request.receiptUrls.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {request.receiptUrls.map((r) =>
                r.url ? (
                  <a
                    key={r.storageId}
                    href={r.url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-block"
                  >
                    <img
                      src={r.url}
                      alt="Receipt"
                      className="h-12 w-12 rounded object-cover ring-1 ring-slate-200 hover:ring-slate-400 transition"
                    />
                  </a>
                ) : null,
              )}
            </div>
          )}
          {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
        </div>
        <div className="shrink-0 flex flex-col items-end gap-1.5">
          {role === "tenant" && request.status === "pending" && (
            <>
              <label className="cursor-pointer">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={(e) =>
                    handleAttachReceipt(e.target.files?.[0] ?? null)
                  }
                  className="sr-only"
                  disabled={busy}
                />
                <span className="inline-block rounded-md border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-medium text-slate-700 hover:bg-slate-50 transition-colors">
                  {busy ? "…" : "Add receipt"}
                </span>
              </label>
              <button
                type="button"
                onClick={handleCancel}
                disabled={busy}
                className="text-[11px] font-medium text-slate-500 hover:text-red-600 px-2 py-1 rounded hover:bg-slate-100 disabled:opacity-50"
              >
                Cancel
              </button>
            </>
          )}
          {role === "landlord" && request.status === "pending" && (
            <>
              <button
                type="button"
                onClick={() =>
                  setDecisionPick(decisionPick === "approved" ? null : "approved")
                }
                disabled={busy}
                className="rounded-md bg-emerald-50 px-2.5 py-1 text-[11px] font-medium text-emerald-700 ring-1 ring-emerald-200 hover:bg-emerald-100 transition-colors disabled:opacity-50"
              >
                Approve…
              </button>
              <button
                type="button"
                onClick={() =>
                  setDecisionPick(decisionPick === "denied" ? null : "denied")
                }
                disabled={busy}
                className="rounded-md bg-rose-50 px-2.5 py-1 text-[11px] font-medium text-rose-700 ring-1 ring-rose-200 hover:bg-rose-100 transition-colors disabled:opacity-50"
              >
                Deny…
              </button>
            </>
          )}
        </div>
      </div>
      {role === "landlord" &&
        request.status === "pending" &&
        decisionPick !== null && (
          <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-2">
            <Field
              label={`${decisionPick === "approved" ? "Approval" : "Denial"} note (optional)`}
            >
              <textarea
                className={`${inputCls} min-h-[64px]`}
                value={decisionNotesDraft}
                onChange={(e) => setDecisionNotesDraft(e.target.value)}
                placeholder={
                  decisionPick === "approved"
                    ? "e.g., Applied as $42 rent credit for May."
                    : "e.g., Item is tenant-replaceable per the lease."
                }
              />
            </Field>
            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setDecisionPick(null);
                  setDecisionNotesDraft("");
                }}
                disabled={busy}
                className="text-xs font-medium text-slate-600 hover:text-slate-900 px-3 py-1.5 rounded hover:bg-slate-100 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleDecide(decisionPick)}
                disabled={busy}
                className={
                  decisionPick === "approved"
                    ? "rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700 transition-colors disabled:opacity-50"
                    : "rounded-md bg-rose-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-rose-700 transition-colors disabled:opacity-50"
                }
              >
                {busy
                  ? "Saving…"
                  : decisionPick === "approved"
                    ? "Approve"
                    : "Deny"}
              </button>
            </div>
          </div>
        )}
    </li>
  );
}

function StatusBadge({ status }: { status: Status }) {
  const styles =
    status === "pending"
      ? "bg-amber-50 text-amber-700 ring-amber-200"
      : status === "approved"
        ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
        : "bg-rose-50 text-rose-700 ring-rose-200";
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide ring-1 ring-inset ${styles}`}
    >
      {status}
    </span>
  );
}

function formatTimestamp(ts: number): string {
  const diff = Date.now() - ts;
  if (diff < 60_000) return "just now";
  if (diff < 3600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86400_000) return `${Math.floor(diff / 3600_000)}h ago`;
  if (diff < 604800_000) return `${Math.floor(diff / 86400_000)}d ago`;
  return new Date(ts).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}
